import { useEffect, useMemo, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../api/axios';
import { storageUrl } from '../utils/urls';

const INITIAL_FORM = {
  company_name: '',
  company_email: '',
  company_phone: '',
  company_logo_path: '',
};

export default function Settings() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get('/settings/company');
        if (!mounted) return;
        setForm({
          company_name: data?.company_name || '',
          company_email: data?.company_email || '',
          company_phone: data?.company_phone || '',
          company_logo_path: data?.company_logo_path || '',
        });
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.message || 'تعذر تحميل إعدادات الشركة.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const logoPreview = useMemo(() => {
    if (logoFile) return URL.createObjectURL(logoFile);
    return storageUrl(form.company_logo_path);
  }, [logoFile, form.company_logo_path]);

  useEffect(() => () => {
    if (logoFile && logoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreview);
    }
  }, [logoFile, logoPreview]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = new FormData();
      payload.append('company_name', form.company_name);
      payload.append('company_email', form.company_email);
      payload.append('company_phone', form.company_phone);
      if (logoFile) {
        payload.append('logo', logoFile);
      }

      const { data } = await api.put('/settings/company', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setForm({
        company_name: data?.company_name || '',
        company_email: data?.company_email || '',
        company_phone: data?.company_phone || '',
        company_logo_path: data?.company_logo_path || '',
      });
      setLogoFile(null);
      setSuccess('تم حفظ إعدادات الشركة بنجاح.');
    } catch (err) {
      setError(err?.response?.data?.message || 'تعذر حفظ إعدادات الشركة.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell title="الإعدادات">
      {loading && <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">جاري تحميل الإعدادات...</div>}
      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {success && <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{success}</div>}

      <div className="bg-white rounded-2xl border p-5 shadow-sm space-y-5">
        <div>
          <h2 className="text-lg font-bold text-slate-900">بيانات شركة الفحص</h2>
          <p className="text-sm text-slate-500 mt-1">هذه البيانات ستستخدم في الترويسة والتقارير.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm text-gray-600">اسم شركة الفحص</span>
            <input className="mt-1 w-full rounded-lg border p-2" value={form.company_name} onChange={(e) => setField('company_name', e.target.value)} />
          </label>

          <label className="block">
            <span className="text-sm text-gray-600">البريد الإلكتروني</span>
            <input type="email" className="mt-1 w-full rounded-lg border p-2" value={form.company_email} onChange={(e) => setField('company_email', e.target.value)} />
          </label>

          <label className="block">
            <span className="text-sm text-gray-600">رقم الهاتف</span>
            <input className="mt-1 w-full rounded-lg border p-2" value={form.company_phone} onChange={(e) => setField('company_phone', e.target.value)} />
          </label>

          <label className="block">
            <span className="text-sm text-gray-600">اللوقو</span>
            <input type="file" accept="image/*" className="mt-1 w-full rounded-lg border p-2" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
          </label>
        </div>

        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4">
          <div className="text-sm font-semibold text-slate-700 mb-3">معاينة اللوقو</div>
          {logoPreview ? (
            <img src={logoPreview} alt="company logo preview" className="h-24 w-24 rounded-xl border bg-white object-contain p-2" />
          ) : (
            <div className="text-sm text-slate-500">لا يوجد لوقو مرفوع بعد.</div>
          )}
        </div>

        <div>
          <button onClick={submit} disabled={saving || loading} className="rounded-xl bg-primary px-6 py-3 text-white font-bold disabled:opacity-50">
            {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
          </button>
        </div>
      </div>
    </AppShell>
  );
}
